import { FC } from "react";

import {
  Add24Regular,
  NavigationRegular,
  QuestionCircleRegular,
  SignOut24Regular,
} from "@fluentui/react-icons";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  makeStyles,
  useRestoreFocusSource,
} from "@fluentui/react-components";

import { translate } from "@/utils/translations";
import { SUPPORT_URL } from "@/constants/constants";

import { Logo } from "./Logo";
import { ThreadsList } from "./ThreadsList";
import { ThreadData } from "../constants/threads";

interface SidebarProps {
  title?: string;
  activeThreadId?: string;
  isOpen: boolean;
  threads?: ThreadData[];
  onClose: () => void;
  onLogout?: () => void;
  onStartNewThread?: () => void;
  onThreadSelect?: (threadId: string) => void;
}

export const Sidebar: FC<SidebarProps> = ({
  activeThreadId,
  isOpen,
  onClose,
  onLogout,
  onStartNewThread,
  onThreadSelect,
  threads = [],
  title = "Menu",
}) => {
  const styles = useStyles();

  const restoreFocusSourceAttributes = useRestoreFocusSource();

  return (
    <Drawer
      className={styles.drawer}
      onOpenChange={(_, { open }) => !open && onClose()}
      open={isOpen}
      position="start"
      separator
      size="small"
      type="overlay"
      {...restoreFocusSourceAttributes}
    >
      <DrawerHeader className={styles.drawerHeader}>
        <DrawerHeaderTitle>
          <Button
            appearance="transparent"
            aria-label="Close"
            className={styles.headerButton}
            icon={<NavigationRegular />}
            onClick={onClose}
          >
            {title}
          </Button>
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBodyContainer}>
        <div className={styles.contentArea}>
          <Button
            appearance="primary"
            className={styles.newThreadButton}
            icon={<Add24Regular />}
            onClick={() => {
              if (onStartNewThread) {
                onStartNewThread();
              }
              onClose();
            }}
          >
            {translate("Start new thread")}
          </Button>

          {/* Threads Section */}
          <ThreadsList
            activeThreadId={activeThreadId}
            onClose={onClose}
            onThreadSelect={onThreadSelect}
            threads={threads}
          />
        </div>

        {/* Support Row */}
        <div className={styles.supportRow}>
          <Button
            appearance="transparent"
            as="a"
            className={styles.supportButton}
            href={SUPPORT_URL}
            icon={<QuestionCircleRegular fontSize={16} />}
            rel="noopener noreferrer"
            target="_blank"
          >
            {translate("Support")}
          </Button>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.logoContainer}>
            <Logo size={18} />
          </div>
          <Button
            appearance="transparent"
            className={styles.logoutButton}
            icon={<SignOut24Regular />}
            onClick={() => {
              if (onLogout) {
                onLogout();
              }
              onClose();
            }}
          >
            {translate("Log out")}
          </Button>
        </div>
      </DrawerBody>
    </Drawer>
  );
};

const useStyles = makeStyles({
  contentArea: {
    flex: "1",
    padding: "0 6px",
  },
  drawer: {
    backgroundColor: "#EBEBEB",
  },
  drawerBodyContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: "0",
  },
  drawerHeader: {
    padding: "8px 0px",
  },
  footer: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginTop: "auto",
    padding: "8px 8px 8px 14px",
  },
  headerButton: {
    "&:focus": {
      boxShadow: "none !important",
      outline: "none !important",
    },
    "&:focus-visible": {
      boxShadow: "none !important",
      outline: "none !important",
    },
  },
  logoContainer: {
    alignItems: "center",
    display: "flex",
  },
  logoutButton: {
    padding: "8px 0px",
  },
  newThreadButton: {
    marginBottom: "16px",
    width: "100%",
  },
  supportButton: {
    fontSize: "14px",
    fontWeight: "500",
    justifyContent: "flex-start",
    textDecoration: "none",
  },
  supportRow: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
});
